import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Check, CirclePlusIcon } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useGqlQuery } from "@/lib/GraphQLCodegen/fetcher";
import { Region } from "@/gql/graphql";
import { cityListQuery } from "@/app/Cities/Cities/GraphQL/cityListQuery";
import { cityNameQuery } from "@/app/Cities/Cities/GraphQL/cityNameQuery";
import { Fragment, useState } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import { useTranslations } from "use-intl";

const showState: {
  [key in Region]: boolean;
} = {
  [Region.Au]: true,
  [Region.Ca]: true,
  [Region.Eu]: true,
  [Region.Jp]: false,
  [Region.Nz]: false,
  [Region.Pi]: true,
  [Region.Sacu]: true,
  [Region.Tw]: false,
  [Region.Us]: true,
  [Region.Sa]: true,
  [Region.Me]: true,
  [Region.Mx]: true,
  [Region.Kr]: true,
};

export function CityFilter({
  placeholder,
  onChange,
  selected,
  regions = [],
}: {
  placeholder: string;
  selected: string[];
  onChange: (selected: string[]) => void;
  regions?: Region[];
}) {
  const t = useTranslations("city.state");
  const numSelected = selected.length;

  const selectedLabel =
    numSelected > 2 ? (
      <>
        <CityLabel id={selected[0]} /> and {numSelected - 1} more
      </>
    ) : (
      selected.map((id, index) => (
        <Fragment key={id}>
          {index > 0 ? ", " : null}
          <CityLabel id={id} />
        </Fragment>
      ))
    );

  const [search, setSearch] = useState("");

  const { data } = useGqlQuery(
    cityListQuery,
    {
      search,
      first: 20,
      page: 1,
      region: regions.length ? regions[0] : undefined,
    },
    {
      placeholderData: keepPreviousData,
    },
  );

  return (
    <Popover>
      <PopoverTrigger className="inline-flex items-center space-x-1 rounded-full border border-dashed border-gray-300 px-2 py-px text-xs transition-all hover:border-gray-500 hover:bg-gray-100">
        <CirclePlusIcon
          onClick={(e) => {
            if (numSelected === 0) return;
            e.preventDefault();
            onChange([]);
          }}
          className={cn("h-3 w-3 transition-all", {
            "rotate-0": numSelected === 0,
            "rotate-45 hover:text-red-500": numSelected > 0,
          })}
        />
        <span className="font-semibold text-gray-700">{placeholder}</span>

        {numSelected > 0 && (
          <span className="border-l border-gray-300 px-1 font-semibold text-indigo-500">
            {selectedLabel}
          </span>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-[250px] p-0" align="start">
        <Command shouldFilter={false}>
          <CommandInput
            placeholder="Search cities..."
            value={search}
            onValueChange={setSearch}
          />
          <CommandList>
            <CommandEmpty>No cities found.</CommandEmpty>
            <CommandGroup>
              {data?.cities.data.map((city) => {
                const isSelected = selected.includes(city.id);

                return (
                  <CommandItem
                    key={city.id}
                    value={city.id}
                    onSelect={(currentValue) => {
                      onChange(
                        selected.includes(currentValue)
                          ? selected.filter((v) => v !== currentValue)
                          : [...selected, currentValue],
                      );
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        isSelected ? "opacity-100" : "opacity-0",
                      )}
                    />
                    <div>
                      <p>{city.name}</p>
                      <p className="text-xs text-gray-500">
                        {city.region}
                        {showState[city.region] ? ` - ${t(city.state)}` : null}
                      </p>
                    </div>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function CityLabel({ id }: { id: string }) {
  const t = useTranslations("city.state");
  const { data } = useGqlQuery(cityNameQuery, { id });

  if (!data) {
    return null;
  }

  const { name, region, state } = data.city;

  return showState[region] ? `${name} (${t(state)})` : name;
}
